/**
 * Link-card floating element renderer (F-31, docs/04 §7) —
 * registries/element-types/linkCard per docs/08 §2: one folder per element type;
 * the canvas layer (ElementsLayer) looks the type up in the registry and knows
 * nothing about link cards specifically. Selection / drag / resize / z-order /
 * delete are the SHARED element machinery (ElementView) — a link card behaves
 * like any other element there; only its interior and the open-the-link action
 * are card-specific.
 *
 * Visual rungs mirror the `LinkCardElementPayload.status` lifecycle:
 *  - "loading": skeleton (spinner + domain) while the server unfurls;
 *  - "ready" + VIDEO (F-31b/c, 2026-07-15): thumbnail + title ONLY — no domain
 *    line, no url line. The whole card is the link, so repeating the host under
 *    a YouTube thumbnail is noise; the thumbnail already says what this is.
 *  - "ready" + ordinary: thumbnail (only if a `thumbnailAssetId` was re-hosted)
 *    + title + favicon + domain — unchanged from before the amendment;
 *  - "error": domain + url, still a clickable card (the domain-only fallback).
 *
 * F-31(a): a ready card's `w`/`h` were sized to the thumbnail's real aspect when
 * the unfurl landed (see aspect.ts + ImageDropLayer's resolveUnfurl), so the
 * thumbnail band fills the card without letterboxing at its natural ratio.
 *
 * All content is `pointer-events: none` (see app.css) so the wrapper element
 * owns the drag AND the double-click-to-open (ElementsLayer guards
 * click-vs-drag) — that is what makes "the whole card is the click target"
 * (F-31c) true here without this renderer wiring any click handler of its own.
 * Where that click GOES is ./open.ts, shared with the toolbar button.
 */
import { clipOf, clipText } from "@kola/shared";

import type { ElementRendererProps } from "../image/ImageElement.js";
import { assetUrl } from "../../../core/api.js";
import { isVideoCard } from "./aspect.js";

/**
 * F-138: the card's clip range, as one line — "clip 1:23 → 1:41". Shown on
 * every rung that has one so the writer sees the range they set without
 * selecting the card; a `t=` read off the URL shows too (the export will
 * use it), in the same words, because the export makes no difference.
 */
function ClipLine({ payload }: { payload: Parameters<typeof clipOf>[0] }) {
  const clip = clipOf(payload);
  if (clip === undefined) return null;
  return <span className="link-card-clip">{clipText(clip)}</span>;
}

/** Host of a URL for the domain-only rung, falling back to the raw string. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export function LinkCardElementView({ element }: ElementRendererProps) {
  const payload = element.payload;
  if (payload.kind !== "linkCard") return null;
  const domain = payload.domain ?? hostOf(payload.url);

  if (payload.status === "loading") {
    return (
      <div className="link-card link-card-loading">
        <span className="link-card-spinner" aria-hidden="true" />
        <span className="link-card-domain">{domain}</span>
      </div>
    );
  }

  if (payload.status === "error") {
    return (
      <div className="link-card link-card-error">
        <span className="link-card-domain">{domain}</span>
        <span className="link-card-url">{payload.url}</span>
        <ClipLine payload={payload} />
      </div>
    );
  }

  // status === "ready"
  const hasThumb = payload.thumbnailAssetId !== undefined && payload.thumbnailAssetId !== "";
  const hasTitle = payload.title !== undefined && payload.title !== "";
  const hasFavicon = payload.favicon !== undefined && payload.favicon !== "";
  const thumb = hasThumb ? (
    <img
      className="link-card-thumb"
      src={assetUrl(payload.thumbnailAssetId as string)}
      alt=""
      draggable={false}
    />
  ) : null;

  // F-31(c): a video card is thumbnail + title, full stop. It deliberately
  // shares no markup with the ordinary rung below — "render the same card but
  // hide two spans" is how the domain line creeps back in.
  if (isVideoCard(payload)) {
    return (
      <div className="link-card link-card-ready link-card-video">
        {thumb}
        {(hasTitle || clipOf(payload) !== undefined) && (
          <div className="link-card-body">
            {hasTitle && <span className="link-card-title">{payload.title}</span>}
            <ClipLine payload={payload} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="link-card link-card-ready">
      {thumb}
      <div className="link-card-body">
        {hasTitle && <span className="link-card-title">{payload.title}</span>}
        <span className="link-card-meta">
          {hasFavicon && (
            <img className="link-card-favicon" src={payload.favicon} alt="" draggable={false} />
          )}
          <span className="link-card-domain">{domain}</span>
        </span>
        <ClipLine payload={payload} />
      </div>
    </div>
  );
}
